// @flow strict

import React, { useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import { FormGridCell, FormGridRow, TextInput, Select2 } from '@ansarada/ace-react';
import styles from './styles.scss';

import type { Option, JobTitleGroup, FormValue, Group } from '../types';
import {
  JobTitleGroupOtherMaxLength,
  JobTitleOtherMaxLength,
  PROFILE_FORM_KEYS as KEYS,
} from '../constants';
import {
  raiseFilledEventIncludeData,
  SupportedTrackingFieldNames,
} from '../../../../core/lib/segment-actions';

type Props = {|
  jobTitleGroups: JobTitleGroup[],
  isShowError: (fieldName: string) => boolean,
  disabled: boolean,
|};

export default function JobTitleInput({ isShowError, jobTitleGroups, disabled }: Props) {
  const { values, errors, setFieldValue } = useFormikContext<FormValue>();
  const [specialties, setSpecialties] = useState<Group[]>([]);
  const [specialtyTitles, setSpecialtyTitles] = useState<Group[]>([]);

  useEffect(() => {
    setSpecialties([
      {
        title: '',
        items: jobTitleGroups.map(group => ({
          value: group.jobTitleGroupName,
          label: group.jobTitleGroupName,
        })),
      },
    ]);
  }, [jobTitleGroups]);

  useEffect(() => {
    if (values.jobTitleGroup === 'Other') {
      setFieldValue(KEYS.jobTitleId, '0');
      setSpecialtyTitles([]);
      return;
    }

    const jobGroup = jobTitleGroups.find(group => group.jobTitleGroupName === values.jobTitleGroup);
    if (jobGroup) {
      const { jobTitles } = jobGroup;
      setFieldValue(KEYS.jobTitleId, '');
      setSpecialtyTitles([
        {
          title: '',
          items: jobTitles.map(title => ({
            value: String(title.jobTitleId),
            label: title.jobTitleName,
          })),
        },
      ]);
    }
  }, [values.jobTitleGroup, jobTitleGroups]);

  return (
    <div
      onBlur={() =>
        raiseFilledEventIncludeData(
          values.jobTitleId,
          SupportedTrackingFieldNames.IncludeData.JobTitle,
        )
      }
      className={styles.inputGroups}
    >
      <FormGridRow key="specialty-selection">
        <FormGridCell
          labelText="Specialty*"
          errors={isShowError(KEYS.jobTitleGroup) ? <span>{errors.jobTitleGroup}</span> : undefined}
          errorPosition="Bottom"
        >
          <Select2
            data-test-id="profile-specialty"
            value={values.jobTitleGroup}
            placeholder="Select specialty"
            options={specialties}
            size="Medium"
            status={isShowError(KEYS.jobTitleGroup) ? 'Bad' : undefined}
            disable={disabled}
            onChange={(item: Option) => setFieldValue(KEYS.jobTitleGroup, item.value)}
          />
        </FormGridCell>
      </FormGridRow>
      {!(values.jobTitleGroup === 'Other') && (
        <FormGridRow key="title-selection">
          <FormGridCell
            labelText="Title*"
            errors={isShowError(KEYS.jobTitleId) ? <span>{errors.jobTitleId}</span> : undefined}
            errorPosition="Bottom"
          >
            <Select2
              data-test-id="profile-specialty-title"
              value={values.jobTitleId}
              placeholder={values.jobTitleGroup ? 'Select job title' : 'Select specialty first'}
              options={specialtyTitles}
              size="Medium"
              status={isShowError(KEYS.jobTitleId) ? 'Bad' : undefined}
              disable={disabled}
              onChange={(item: Option) => setFieldValue(KEYS.jobTitleId, item.value)}
            />
          </FormGridCell>
        </FormGridRow>
      )}
      {values.jobTitleGroup === 'Other' && (
        <>
          <FormGridRow key="profession">
            <FormGridCell
              labelText="Profession*"
              errors={
                isShowError(KEYS.jobTitleGroupOther) ? (
                  <span>{errors.jobTitleGroupOther}</span>
                ) : (
                  undefined
                )
              }
              errorPosition="Bottom"
            >
              <TextInput
                data-test-id="job-title-group-other"
                type="Text"
                maxLength={JobTitleGroupOtherMaxLength}
                value={values.jobTitleGroupOther}
                status={isShowError(KEYS.jobTitleGroupOther) ? 'Bad' : undefined}
                disabled={disabled}
                onChangeValue={value => setFieldValue(KEYS.jobTitleGroupOther, value)}
              />
            </FormGridCell>
          </FormGridRow>
          <FormGridRow key="jobTitle">
            <FormGridCell
              labelText="Title*"
              errors={
                isShowError(KEYS.jobTitleOther) ? <span>{errors.jobTitleOther}</span> : undefined
              }
              errorPosition="Bottom"
            >
              <TextInput
                data-test-id="job-title-other"
                type="Text"
                maxLength={JobTitleOtherMaxLength}
                value={values.jobTitleOther}
                status={isShowError(KEYS.jobTitleOther) ? 'Bad' : undefined}
                disabled={disabled}
                onChangeValue={value => setFieldValue(KEYS.jobTitleOther, value)}
              />
            </FormGridCell>
          </FormGridRow>
        </>
      )}
    </div>
  );
}
